import { Pencil, Trash2, Check, X, Star, Gift } from "lucide-react";
import { resolveImageUrl, PLACEHOLDER } from "@/utils/imageUrl";
import { getProductAge, productProfit } from "@/utils/adminHelpers";

const AdminProductCard = ({ product, onEdit, onMarkSold, onMarkUnsold, onDelete }) => {
  const profit = productProfit(product);
  const profitClass = profit >= 0 ? "text-[#00FF66]" : "text-red-400";
  return (
    <div className="glass-card overflow-hidden group" data-testid={`admin-product-${product.id}`}>
      <div className="aspect-square bg-[#050505] relative overflow-hidden">
        <img
          src={resolveImageUrl(product.image_url) || PLACEHOLDER}
          alt={product.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = PLACEHOLDER; }}
        />
        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
          {!product.sold && (
            <>
              <button onClick={() => onEdit(product)} className="bg-white text-black p-2 rounded-lg hover:bg-[#00FF66] transition-colors" data-testid={`edit-btn-${product.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => onMarkSold(product)} className="bg-[#00FF66] text-[#050505] p-2 rounded-lg hover:bg-[#00cc55] transition-colors" data-testid={`sold-btn-${product.id}`}><Check className="w-4 h-4" /></button>
            </>
          )}
          {product.sold && (
            <button onClick={() => onMarkUnsold(product)} className="bg-[#B026FF] text-white p-2 rounded-lg hover:bg-[#9020dd] transition-colors" data-testid={`unsold-btn-${product.id}`}><X className="w-4 h-4" /></button>
          )}
          <button onClick={() => onDelete(product)} className="bg-white text-black p-2 rounded-lg hover:bg-red-500 hover:text-white transition-colors" data-testid={`delete-btn-${product.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1 mb-0.5">
          <p className="text-sm font-medium truncate text-white flex-1">{product.name}</p>
          {product.exclusive && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0" />}
        </div>
        <p className="text-xs text-[#A0A0A0]">{product.size} &bull; {product.category}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[#00FF66] font-oswald font-semibold">${product.sale_price}</span>
          {product.buy_price > 0 && (
            <span className={`text-xs font-medium ${profitClass}`} data-testid={`profit-${product.id}`}>
              {profit >= 0 ? "+" : ""}${profit.toFixed(0)}
            </span>
          )}
        </div>
        {product.buy_price_usd > 0 && <p className="text-xs text-[#A0A0A0] mt-1">Costo: ${product.buy_price_usd} USD</p>}
        <p className="text-xs text-[#A0A0A0]/60 mt-1" data-testid={`age-${product.id}`}>{getProductAge(product.created_at)}</p>
        {product.sold && <span className="inline-block mt-1 text-xs bg-[#B026FF]/20 text-[#B026FF] px-2 py-0.5 rounded-full font-medium">VENDIDO</span>}
        {product.sold && product.referral_code && (
          <span className="inline-flex items-center gap-1 mt-1 ml-1 text-xs bg-[#00FF66]/10 text-[#00FF66] px-2 py-0.5 rounded-full font-mono" data-testid={`referral-badge-${product.id}`}>
            <Gift className="w-3 h-3" /> {product.referral_code}
          </span>
        )}
      </div>
    </div>
  );
};

export const ProductGrid = ({ products, totalInTab, activeTab, onClearFilters, onAdd, ...handlers }) => {
  if (products.length === 0) {
    return (
      <div className="text-center py-20 glass-card">
        {totalInTab > 0 ? (
          <>
            <p className="text-[#A0A0A0] mb-4">No hay productos que coincidan con tu busqueda</p>
            <button onClick={onClearFilters} className="text-[#00FF66] text-sm hover:underline" data-testid="admin-empty-clear-filters">Limpiar filtros</button>
          </>
        ) : (
          <>
            <p className="text-[#A0A0A0] mb-4">{activeTab === "active" ? "No hay productos activos" : "No hay productos vendidos"}</p>
            {activeTab === "active" && <button onClick={onAdd} className="bg-[#00FF66] text-[#050505] px-6 py-2 font-bold rounded-full">Agregar Producto</button>}
          </>
        )}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {products.map(product => <AdminProductCard key={product.id} product={product} {...handlers} />)}
    </div>
  );
};
